import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Typography,
  Menu,
  MenuItem,
  Popover,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { DownArrowIcon } from "../../assets/Icon/ArrowIcon";
import { LogoutIcon } from "../../assets/Icon/dashboredIcon";
import logo2 from "../../assets/Images/logoIMg.png";
import LogOutModal from "../auth-form/Logout";
import theme from "../../theme";
import { useDispatch } from "react-redux";
import { GetAdminNotifications, MarkNotifications } from "../../stores/admin";
import { formatToTimeZone } from "../../utils/functions";
import { setSelectedOrder } from "../../stores/orders";

export default function BasicPopover() {
  const dispatch = useDispatch();
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [fullName, setFullName] = React.useState("John D");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [notificationAnchor, setNotificationAnchor] =
    useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchor);
  const id = open ? "account-popover" : undefined;
  const notificationId = notificationOpen ? "notification-popover" : undefined;

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setOffset(0);
  };

  useEffect(() => {
    getNotifications();
  }, [tabValue]);

  useEffect(() => {
    if (offset > 0) {
      getNotifications(true);
    }
  }, [offset]);

  useEffect(() => {
    const firstName = localStorage.getItem("firstName") || "John";
    const lastName = localStorage.getItem("lastName") || "D";
    setFullName(`${firstName} ${lastName}`);
  }, []);

  const getNotifications = async (append = false) => {
    try {
      if (append) {
        setIsFetchingMore(true);
      } else {
        setLoading(true);
        setHasMore(true);
      }

      const response = await dispatch<any>(
        GetAdminNotifications({
          type: tabValue ? "unread" : "all",
          offset: offset,
        })
      );

      const notifications = response?.payload?.data || [];

      if (offset === 0 && notifications?.length === 0) {
        setData([]);
        return;
      }

      // 🛑 If API returned no more notifications, stop further calls
      if (notifications.length === 0) {
        setHasMore(false);
        return;
      }

      setData((prev) => (append ? [...prev, ...notifications] : notifications));

      const unread = (
        append ? [...data, ...notifications] : notifications
      ).filter((item: any) => item?.marked_as_read === false);
      setUnreadNotifications(unread);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  };

  const handleNotificationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleMark = async (item: any) => {
    if (!item?.order_item_id) {
      getNotifications();
      window.location.href = item?.notification_path;
      return;
    } else {
      await dispatch<any>(
        MarkNotifications({ order_item_id: item?.order_item_id })
      );

      window.location.href = item?.notification_path;
      getNotifications();
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {/* 🔔 Notification Button */}
      <Button
        onClick={handleNotificationClick}
        sx={{
          position: "relative",
          minWidth: "auto",
          p: 0,
          mr: 1,
          background: "none",
          boxShadow: "none",
          "&:hover": { background: "transparent" },
        }}
      >
        {/* 🔴 Red dot indicator */}
        {unreadNotifications?.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 5,
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "red",
            }}
          />
        )}

        <NotificationsNoneOutlinedIcon
          style={{
            color: "#00000090",
            fontSize: "26px",
            cursor: "pointer",
          }}
        />
      </Button>

      {/* 🪄 Notification Popover */}
      <Popover
        id={notificationId}
        open={notificationOpen}
        anchorEl={notificationAnchor}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: "520px",
            borderRadius: "12px",
            bgcolor: "#fff",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            height: "600px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* 🔹 Fixed Header */}
        <Box
          sx={{
            flexShrink: 0,
            background: "#fff",
            zIndex: 10,
            position: "sticky",
            top: 0,
            padding: "20px",
            height: "95px",
            width: "520px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
              All Notifications
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                color: "#918c8c",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={async () => {
                await dispatch<any>(MarkNotifications({ order_item_id: null }));
                getNotifications();
              }}
            >
              Mark all as read
            </Typography>
          </Box>

          {/* 🔹 Tabs (also sticky below header) */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              background: "#fff",
              width: "520px",
              "& .MuiTabs-flexContainer": {
                justifyContent: "flex-start",
                pt: 2,
                m: 0,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                color: "#555",
                padding: 0,
                minHeight: "unset",
                minWidth: "unset",
                marginRight: "30px",
              },
              "& .Mui-selected": {
                color: "#2977CC",
              },
            }}
          >
            <Tab label="All" />
            <Tab label="Unread" />
          </Tabs>
        </Box>

        {/* Scrollable Notifications List */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            mt: 0,
          }}
          onScroll={(e) => {
            const target = e.currentTarget;
            const isBottom =
              target.scrollHeight - target.scrollTop === target.clientHeight;

            if (isBottom && !isFetchingMore && hasMore) {
              setOffset((prev) => prev + 10);
            }
          }}
        >
          {loading ? (
            // ✅ Loader shown when loading is true
            <Box
              sx={{
                height: "505px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={30} sx={{ color: "#1976d2" }} />
            </Box>
          ) : tabValue === 0 ? (
            <div>
              {data?.length ? (
                data.map((item: any) => (
                  <Typography
                    key={item?.id}
                    sx={{
                      color: "#666",
                      display: "flex",
                      backgroundColor: item?.marked_as_read
                        ? "#fff"
                        : "#ededed",
                      borderBottom: "1px solid #ccc",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <Box
                      sx={{ p: 2, width: "100%" }}
                      onClick={async () => {
                        if (
                          !item?.marked_as_read &&
                          item?.order_item_id &&
                          item?.notification_path
                        ) {
                          await dispatch<any>(
                            MarkNotifications({
                              order_item_id: item?.order_item_id,
                            })
                          );
                          getNotifications();
                          await dispatch<any>(
                            setSelectedOrder({
                              orderId: item?.order_item_id,
                              productId: item?.product_id,
                              userId: item?.sender_id,
                            })
                          );
                          window.location.href = item?.notification_path;
                        } else if (!item?.order_item_id) {
                          getNotifications();
                          window.location.href = item?.notification_path;
                        } else if (item?.notification_path) {
                          await dispatch<any>(
                            setSelectedOrder({
                              orderId: item?.order_item_id,
                              productId: item?.product_id,
                              userId: item?.sender_id,
                            })
                          );
                          window.location.href = item?.notification_path;
                          getNotifications();
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "black", fontSize: 16 }}
                        >
                          {item?.sender_name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {!item?.marked_as_read && (
                            <span
                              style={{
                                width: "6px",
                                height: "6px",
                                backgroundColor: "#2977CC",
                                display: "inline-block",
                                borderRadius: "50%",
                                marginRight: "6px",
                              }}
                            ></span>
                          )}{" "}
                          {formatToTimeZone(item?.created_at)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 14, mt: 0.5 }}>
                        {item?.notification_data}
                      </Typography>
                    </Box>
                  </Typography>
                ))
              ) : (
                <Typography
                  sx={{
                    height: "505px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#636363",
                    py: 10,
                  }}
                >
                  No notifications!
                </Typography>
              )}
            </div>
          ) : data?.length ? (
            data?.map((item: any) => (
              <Typography
                key={item?.id}
                sx={{
                  color: "#666",
                  display: "flex",
                  backgroundColor: "#ededed",
                  borderBottom: "1px solid #ccc",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <Box
                  sx={{ p: 2, width: "100%" }}
                  onClick={() => handleMark(item)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 600, color: "black", fontSize: 16 }}
                    >
                      {item?.sender_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                      {formatToTimeZone(item?.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, mt: 0.5 }}>
                    {item?.notification_data}
                  </Typography>
                </Box>
              </Typography>
            ))
          ) : (
            <Typography
              sx={{
                height: "505px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#636363",
                py: 10,
              }}
            >
              No unread notifications!
            </Typography>
          )}
        </Box>
        {isFetchingMore && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 0.5,
            }}
          >
            <CircularProgress size={24} sx={{ color: "#1976d2" }} />
          </Box>
        )}
      </Popover>

      {/* 👤 User Button */}
      <Button
        aria-describedby={id}
        variant="contained"
        onClick={handleClick}
        sx={{
          gap: "10px",
          background: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        }}
      >
        <Avatar alt="Cindy Baker" src={logo2} sx={{ width: 41, height: 41 }} />
        <Typography
          sx={{
            display: { xs: "none", md: "flex" },
            fontSize: "16px",
            fontWeight: 600,
            color: theme.palette.myCustomColor.dark,
          }}
        >
          {fullName}
        </Typography>
        <DownArrowIcon />
      </Button>

      {/* Account Menu */}
      {!showLogoutModal && (
        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            "& .MuiPaper-root": {
              width: 180,
              borderRadius: "10px",
              border: "0.5px solid #A7C7EA",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              "& .MuiMenu-list": { padding: 0 },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setShowLogoutModal(true);
              handleClose();
            }}
            sx={{ display: "flex", gap: "10px", padding: 2 }}
          >
            <LogoutIcon />
            <Typography
              sx={{ fontSize: "16px", fontWeight: 500, color: "#2C3034" }}
            >
              Log Out
            </Typography>
          </MenuItem>
        </Menu>
      )}

      <LogOutModal
        open={showLogoutModal}
        handleClose={() => setShowLogoutModal(false)}
      />
    </div>
  );
}
